import type { ApolloCache, Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { client } from '~publish/legacy/apollo-client'
import { logError } from '~publish/legacy/utils/logError'
import type { GetPostList } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

const SwapPostsInQueue = graphql(/* GraphQL */ `
  mutation SwapPostsInQueue($input: SwapPostsInQueueInput!) {
    swapPostsInQueue(input: $input) {
      __typename
      ... on SwapPostsInQueueSuccess {
        posts {
          __typename
          id
          dueAt
          status
          isCustomScheduled
          isPinned
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

const SourcePost = graphql(/* GraphQL */ `
  fragment SourcePost on Post {
    id
    dueAt
    status
    isCustomScheduled
    isPinned
  }
`)

const TargetPost = graphql(/* GraphQL */ `
  fragment TargetPost on Post {
    id
    dueAt
    status
    isCustomScheduled
    isPinned
  }
`)

type SwapPostsInQueueInput = VariablesOf<typeof SwapPostsInQueue>['input']
type SwapPostsInQueueMutation = ResultOf<typeof SwapPostsInQueue>
type SwappablePost = ResultOf<typeof SourcePost>

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useSwapPostsMutation = () =>
  useTypedMutation(SwapPostsInQueue, (data) => data.swapPostsInQueue, {
    successTypename: 'SwapPostsInQueueSuccess',
    optimisticResponse: buildOptimisticResponse,
    update: updateCache,
  })

function buildOptimisticResponse({
  input,
}: {
  input: SwapPostsInQueueInput
}): SwapPostsInQueueMutation {
  const sourcePost = client.cache.readFragment<SwappablePost>({
    id: client.cache.identify({ __typename: 'Post', id: input.sourceId }),
    fragment: SourcePost,
  })

  const targetPost = client.cache.readFragment<SwappablePost>({
    id: client.cache.identify({ __typename: 'Post', id: input.targetId }),
    fragment: TargetPost,
  })

  if (!sourcePost || !targetPost) {
    throw new Error('Unable to find posts in cache')
  }

  return {
    swapPostsInQueue: {
      __typename: 'SwapPostsInQueueSuccess',
      posts: [
        {
          __typename: 'Post',
          id: input.sourceId,
          dueAt: targetPost.dueAt,
          status: targetPost.status,
          isCustomScheduled: targetPost.isCustomScheduled,
          isPinned: targetPost.isPinned,
        },
        {
          __typename: 'Post',
          id: input.targetId,
          dueAt: sourcePost.dueAt,
          status: sourcePost.status,
          isCustomScheduled: sourcePost.isCustomScheduled,
          isPinned: sourcePost.isPinned,
        },
      ],
    },
  }
}

function updateCache(
  cache: ApolloCache,
  { data }: { data?: SwapPostsInQueueMutation | null },
  { variables }: { variables?: { input: SwapPostsInQueueInput } | null },
): void {
  if (!variables) return

  const sourcePost = cache.readFragment<SwappablePost>({
    id: cache.identify({ __typename: 'Post', id: variables.input.sourceId }),
    fragment: SourcePost,
  })

  const targetPost = cache.readFragment<SwappablePost>({
    id: cache.identify({ __typename: 'Post', id: variables.input.targetId }),
    fragment: TargetPost,
  })

  if (!sourcePost || !targetPost) {
    logError(new Error('The posts were not found in the Apollo cache.'), {
      context: 'swapPostsInQueue',
      metaData: {
        sourceId: variables.input.sourceId,
        targetId: variables.input.targetId,
      },
    })
    return
  }

  const swappedPosts =
    data?.swapPostsInQueue.__typename === 'SwapPostsInQueueSuccess'
      ? data.swapPostsInQueue.posts
      : [
          { ...sourcePost, dueAt: targetPost.dueAt },
          { ...targetPost, dueAt: sourcePost.dueAt },
        ]

  cache.modify({
    fields: {
      posts: (
        existingPosts: Reference | StoreObject,
        { readField, toReference }: ModifierDetails,
      ) => {
        const edges = readField<ResultOf<typeof GetPostList>['posts']['edges']>(
          'edges',
          existingPosts,
        )
        if (!edges) return existingPosts

        const newEdges = edges.map((edge) => {
          const postId = readField<string>('id', edge.node)
          const swappedPost = swappedPosts.find((post) => post.id === postId)
          if (swappedPost) {
            return Object.assign({}, edge, {
              node: toReference({
                __typename: 'Post',
                id: postId,
                dueAt: swappedPost.dueAt,
                status: swappedPost.status,
                isCustomScheduled: swappedPost.isCustomScheduled,
                isPinned: swappedPost.isPinned,
              }),
            })
          }
          return edge
        })

        newEdges.sort((a, b) => {
          const aDueAt = readField<string>('dueAt', a.node) ?? ''
          const bDueAt = readField<string>('dueAt', b.node) ?? ''
          return new Date(aDueAt).getTime() - new Date(bDueAt).getTime()
        })

        return {
          ...existingPosts,
          edges: newEdges,
        }
      },
    },
  })
}
